import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { fetchWithRetryAndCache, clearCache } from "../utils/apiCache.js";
import rtoAPI from "../api/rtoAPI";
import "./Inventory.css";
import { useMasterPins } from "../context/PinContext";
import { getEmployeeIdForProtectedAction } from "../utils/masterPinActions";
import { logEmployeeActivity } from "../utils/activityLogger";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "https://techiohisab.com/api";
const INVENTORY_API = `${API_BASE}/inventory`;
const ROOMS_API = `${API_BASE}/rooms`;
const SUPPLIERS_API = `${API_BASE}/suppliers`;
const PURCHASE_HISTORY_API = `${API_BASE}/purchases`;
const INVENTORY_HISTORY_API = `${API_BASE}/inventory-history`;
const CATEGORY_STORAGE_KEY = "inventory_custom_categories";
const STATIC_INVENTORY = [
  {
    id: 1,
    name: "Sample Product 1",
    category: "Electronics",
    stock: 10,
    price: 1000,
    room_id: 1,
    barcode: "123456789",
    created_at: new Date().toISOString(),
  },
  {
    id: 2,
    name: "Sample Product 2",
    category: "Furniture",
    stock: 5,
    price: 5000,
    room_id: 2,
    barcode: "987654321",
    created_at: new Date().toISOString(),
  },
];
const STATIC_ROOMS = [
  { id: 1, name: "Room A", capacity: 100 },
  { id: 2, name: "Room B", capacity: 50 },
];
const STATIC_SUPPLIERS = [
  {
    id: 1,
    name: "Supplier A",
    contact: "1234567890",
    email: "supplierA@example.com",
  },
  {
    id: 2,
    name: "Supplier B",
    contact: "9876543210",
    email: "supplierB@example.com",
  },
];

const normalizeRoomRecord = (room) => ({
  ...room,
  room_name: room?.room_name || room?.name || "",
});

export default function Inventory() {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyPinOverride } = useMasterPins();
  const [activeTab, setActiveTab] = useState("inventory");
  
  // AbortController for cancelling requests on unmount
  const abortControllerRef = useRef(null);

  // Handle tab from URL query parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab) {
      setActiveTab(tab);
    }
  }, [location.search]);

  // Cleanup function to cancel pending requests
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const [items, setItems] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pendingTransfers, setPendingTransfers] = useState([]);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferLoading, setTransferLoading] = useState(false);
  const [showEvaluationModal, setShowEvaluationModal] = useState(false);
  const [evaluationPin, setEvaluationPin] = useState(["", "", "", ""]);
  const [evaluationUnlocked, setEvaluationUnlocked] = useState(false);
  const [evaluationError, setEvaluationError] = useState("");
  const pinInputRefs = useRef([]);
  const [showPriceUnlockModal, setShowPriceUnlockModal] = useState(false);
  const [pricePin, setPricePin] = useState(["", "", "", ""]);
  const [pricesUnlocked, setPricesUnlocked] = useState(false);
  const [priceUnlockError, setPriceUnlockError] = useState("");
  const pricePinInputRefs = useRef([]);
  const [showAddPinModal, setShowAddPinModal] = useState(false);
  const [addPin, setAddPin] = useState(["", "", "", ""]);
  const [addPinError, setAddPinError] = useState("");
  const [addPinVerifying, setAddPinVerifying] = useState(false);
  const addPinInputRefs = useRef([]);
  const employeeId = getEmployeeIdForProtectedAction();

  const [newItem, setNewItem] = useState({
    name: "",
    category: "",
    stock: "",
    price: "",
    room_id: "",
    barcode: "",
    supplier_id: ""
  });

  const [editItem, setEditItem] = useState(null);
  const [newRoom, setNewRoom] = useState("");
  const [editRoomId, setEditRoomId] = useState(null);
  const [editRoomName, setEditRoomName] = useState("");
  const [customCategories, setCustomCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [editCategoryName, setEditCategoryName] = useState("");
  const [editCategoryKey, setEditCategoryKey] = useState(null);
  const [newSupplier, setNewSupplier] = useState({
    name: "",
    contact: "",
    email: "",
    address: ""
  });

  useEffect(() => {
    try {
      const storedCategories = localStorage.getItem(CATEGORY_STORAGE_KEY);
      if (storedCategories) {
        const parsedCategories = JSON.parse(storedCategories);
        setCustomCategories(Array.isArray(parsedCategories) ? parsedCategories : []);
      }
    } catch (err) {
      console.error("Error loading categories from storage:", err);
      setCustomCategories([]);
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(CATEGORY_STORAGE_KEY, JSON.stringify(customCategories));
    } catch (err) {
      console.error("Error saving categories to storage:", err);
    }
  }, [customCategories]);

  // scan + filters
  const [scanCode, setScanCode] = useState("");
  const [filterName, setFilterName] = useState("");
  const [filterBarcode, setFilterBarcode] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [filterAgeBucket, setFilterAgeBucket] = useState("");
  const [filterRoom, setFilterRoom] = useState("");
  const [highlightId, setHighlightId] = useState(null);

  const CLEAN_AGE_BUCKET_OPTIONS = [
    "0-15 days",
    "16-30 days",
    "31-60 days",
    "61-120 days",
    "121-180 days",
    "180+ days",
    "N/A",
  ];

  /* ================= AGE HELPERS ================= */
  const getAgeDays = (createdAt) => {
    if (!createdAt) return null;
    const created = new Date(createdAt);
    const today = new Date();
    return Math.floor((today - created) / (1000 * 60 * 60 * 24));
  };

  const getCleanAgeBucket = (days) => {
    if (days === null) return "N/A";
    if (days <= 15) return "0-15 days";
    if (days <= 30) return "16-30 days";
    if (days <= 60) return "31-60 days";
    if (days <= 120) return "61-120 days";
    if (days <= 180) return "121-180 days";
    return "180+ days";
  };

  const getCleanBucketClass = (bucket) => {
    if (bucket === "0-15 days") return "bucket-0-15";
    if (bucket === "16-30 days") return "bucket-16-30";
    if (bucket === "31-60 days") return "bucket-31-60";
    if (bucket === "61-120 days") return "bucket-61-120";
    if (bucket === "121-180 days") return "bucket-121-180";
    if (bucket === "180+ days") return "bucket-180-plus";
    return "bucket-na";
  };

  // Memoized fetch functions with useCallback and shared cache
  const fetchInventory = useCallback(async () => {
    try {
      // Clear cache to force fresh data
      clearCache(INVENTORY_API);
      
      const res = await fetchWithRetryAndCache(INVENTORY_API, 3, { 
        signal: abortControllerRef.current?.signal 
      });
      
      // Handle multiple response formats
      const data = res.data?.inventory || res.data || STATIC_INVENTORY;
      console.log("?? Inventory API response:", res.data);
      console.log("?? Mapped inventory data:", data);
      
      setItems(data);
    } catch (err) {
      if (err.code !== "ERR_CANCELED") {
  console.error("Error fetching inventory:", err);
}
      // Handle 502 Bad Gateway specifically
      if (err.response?.status === 502 || err.code === 'ERR_NETWORK') {
        console.log("Server down, using static data");
        setItems(STATIC_INVENTORY);
      } else {
        setItems(STATIC_INVENTORY);
      }
    }
  }, []);

  const fetchRooms = useCallback(async () => {
    try {
      const res = await fetchWithRetryAndCache(ROOMS_API, 3, { 
        signal: abortControllerRef.current?.signal 
      });
      const roomData = res.data?.rooms || res.data?.data || res.data || STATIC_ROOMS;
      const normalizedRooms = (Array.isArray(roomData) ? roomData : STATIC_ROOMS).map(normalizeRoomRecord);
      setRooms(normalizedRooms);
    } catch (err) {
      if (err.code !== "ERR_CANCELED") {
  console.error("Error fetching rooms:", err);
}
      // Handle 502 Bad Gateway specifically
      if (err.response?.status === 502 || err.code === 'ERR_NETWORK') {
        console.log("Server down, using static data");
        setRooms(STATIC_ROOMS.map(normalizeRoomRecord));
      } else {
        setRooms(STATIC_ROOMS.map(normalizeRoomRecord));
      }
    }
  }, []);

  const fetchSuppliers = useCallback(async () => {
    try {
      const res = await axios.get(SUPPLIERS_API);
      const data = Array.isArray(res.data) ? res.data : STATIC_SUPPLIERS;
      const normalizedSuppliers = data.map((supplier) => ({
        ...supplier,
        id: supplier.id,
        name:
          supplier.name ||
          supplier.supplier_name ||
          supplier.company_name ||
          `Supplier ${supplier.id}`,
        contact: supplier.contact || supplier.mobile || "",
        email: supplier.email || "",
        address: supplier.address || "",
      }));

      console.log("?? Suppliers API response:", res.data);
      console.log("?? Normalized suppliers:", normalizedSuppliers);
      setSuppliers(normalizedSuppliers);
    } catch (err) {
      console.error("Error fetching suppliers:", err);
      setSuppliers(STATIC_SUPPLIERS);
    }
  }, []);

  const fetchPurchaseHistory = useCallback(async () => {
    try {
      const res = await fetchWithRetryAndCache(PURCHASE_HISTORY_API, 3, { 
        signal: abortControllerRef.current?.signal 
      });
      setPurchaseHistory(res.data || []);
    } catch (err) {
      if (err.code !== "ERR_CANCELED") {
  console.error("Error fetching purchase history:", err);
}
      // Handle 502 Bad Gateway specifically
      if (err.response?.status === 502 || err.code === 'ERR_NETWORK') {
        console.log("Server down, using empty data");
        setPurchaseHistory([]);
      } else {
        setPurchaseHistory([]);
      }
    }
  }, []);

  const fetchPendingTransfers = useCallback(async () => {
    try {
      const data = await rtoAPI.getTransferRequests("inventory");
      setPendingTransfers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching inventory transfer requests:", err);
      setPendingTransfers([]);
    }
  }, []);

  // Refresh function to manually trigger data refresh
  const refreshData = useCallback(async () => {
    // Cancel any ongoing requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new AbortController
    abortControllerRef.current = new AbortController();
    
    try {
      setLoading(true);
      setError(null);

      // Clear cache to force fresh data
      clearCache(INVENTORY_API);
      clearCache(ROOMS_API);
      clearCache(SUPPLIERS_API);
      clearCache(PURCHASE_HISTORY_API);

      // Fetch all data with individual error handling
      await Promise.allSettled([
        fetchInventory(),
        fetchRooms(),
        fetchSuppliers(),
        fetchPurchaseHistory(),
        fetchPendingTransfers()
      ]);

    } catch (err) {
      if (err.code !== "ERR_CANCELED") {
  console.error("Error refreshing data:", err);
}
      setError("Failed to refresh data");
    } finally {
      setLoading(false);
    }
  }, [fetchInventory, fetchRooms, fetchSuppliers, fetchPurchaseHistory, fetchPendingTransfers]);

  // Main data fetching effect with proper dependencies and cleanup
  useEffect(() => {
    refreshData();
    
    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [refreshData]);

  /* ================= INVENTORY CRUD ================= */
  const submitAddItem = async () => {

    // Check for existing item to update stock
    const existingItem = items.find(item => 
      item.name.toLowerCase().trim() === newItem.name.toLowerCase().trim() && 
      item.category.toLowerCase().trim() === newItem.category.toLowerCase().trim()
    );

    // Validate required fields
    if (!newItem.name.trim()) {
      alert("Product name is required");
      return;
    }
    if (!newItem.category.trim()) {
      alert("Category is required");
      return;
    }
    if (!newItem.stock || Number(newItem.stock) <= 0) {
      alert("Stock must be greater than 0");
      return;
    }
    if (!newItem.price || Number(newItem.price) <= 0) {
      alert("Price must be greater than 0");
      return;
    }

    try {
      let res;
      let updatedStock;
      let stockAdded = Number(newItem.stock);

      if (existingItem) {
        // Update existing item stock
        updatedStock = Number(existingItem.stock) + stockAdded;
        console.log(`Updating existing item "${existingItem.name}" - Old stock: ${existingItem.stock}, Adding: ${stockAdded}, New stock: ${updatedStock}`);
        
        res = await axios.put(`${INVENTORY_API}/${existingItem.id}`, {
          name: existingItem.name,
          category: existingItem.category,
          stock: updatedStock,
          price: Number(newItem.price) || Number(existingItem.price),
          room_id: existingItem.room_id,
          barcode: existingItem.barcode,
        });

        console.log("Item updated response:", res.data);
        
        // ? ALWAYS REFRESH FROM SERVER - Don't use local state updates
        // This handles both new items and stock updates correctly
        console.log("? Existing item updated, will refresh from server");
      } else {
        // Add new item
        console.log("Adding new item:", newItem);
        res = await axios.post(INVENTORY_API, {
          ...newItem,
          stock: Number(newItem.stock),
          price: Number(newItem.price),
          room_id: newItem.room_id || null,
          barcode: newItem.barcode || null,
        });

        console.log("Item added response:", res.data);
        updatedStock = Number(newItem.stock);
        
        // ? ALWAYS REFRESH FROM SERVER - Don't use local state updates
        console.log("? New item added, will refresh from server");
      }

    
    // ? LOG INVENTORY HISTORY (IN transaction)
try {
  const targetItem = existingItem || {
  id: res.data.id,
  name: newItem.name,
  category: newItem.category
};
  
  await axios.post(INVENTORY_HISTORY_API, {
    inventory_id: targetItem.id,
    product_name: targetItem.name,
    category: targetItem.category,
    total_in: stockAdded,
    total_out: 0,
    balance: updatedStock,
    last_source: existingItem ? "Stock Addition" : "Purchase",
    last_updated: new Date().toISOString(),
  });

  console.log(" Inventory history saved");

  // ? LOG PURCHASE HISTORY (only for new purchases)
  if (!existingItem && newItem.supplier_id) {
    try {
      const purchasePayload = {
        inventory_id: targetItem.id,
        supplier_id: newItem.supplier_id,
        product_name: targetItem.name,
        category: targetItem.category,
        quantity: stockAdded,
        unit_price: Number(newItem.price),
        total_amount: stockAdded * Number(newItem.price),
        purchase_date: new Date().toISOString(),
        notes: "Added via inventory form"
      };
      console.log("?? Purchase payload:", purchasePayload);
      await axios.post(PURCHASE_HISTORY_API, purchasePayload);
      console.log("?? Purchase history saved");
    } catch (purchaseErr) {
      console.error("? Purchase history logging failed:", purchaseErr);
      // Don't block the main flow if purchase logging fails
    }
  }
} catch (historyErr) {
  console.error("? Inventory history logging failed:", historyErr);
}

      // ? INSTANT TABLE REFRESH - Re-fetch inventory to ensure UI is up-to-date
      await fetchInventory();
      console.log("? Inventory refreshed after add");

      setNewItem({
        name: "",
        category: "",
        stock: "",
        price: "",
        room_id: "",
        barcode: "",
        supplier_id: ""
      });

      // ? SUCCESS MESSAGE - No need to refresh since UI updates instantly
      void logEmployeeActivity({
        action: existingItem ? "Inventory Updated" : "Inventory Added",
        module: "Inventory",
        description: existingItem
          ? `Added ${stockAdded} units to "${existingItem.name}". Stock is now ${updatedStock}.`
          : `Added inventory item "${newItem.name}" with stock ${updatedStock}.`,
      });

      alert(existingItem ? 
        `? Stock updated successfully! Added ${stockAdded} units to "${existingItem.name}". New stock: ${updatedStock}` : 
        `? Item "${newItem.name}" added successfully!`
      );
    } catch (err) {
      console.error("Error adding item:", err);
      
      // Handle specific duplicate error
      if (err?.response?.data?.message?.includes("already exists")) {
        alert("Product with same name & category already exists. Please use a different name or category.");
      } else {
        alert("Failed to add item: " + (err?.response?.data?.message || err?.message));
      }
    }
  };

  const handleAddItem = (event) => {
    event.preventDefault();

    if (!newItem.name.trim()) {
      alert("Product name is required");
      return;
    }
    if (!newItem.category.trim()) {
      alert("Category is required");
      return;
    }
    if (!newItem.stock || Number(newItem.stock) <= 0) {
      alert("Stock must be greater than 0");
      return;
    }
    if (!newItem.price || Number(newItem.price) <= 0) {
      alert("Price must be greater than 0");
      return;
    }

    setAddPin(["", "", "", ""]);
    setAddPinError("");
    setShowAddPinModal(true);
    setTimeout(() => addPinInputRefs.current[0]?.focus(), 0);
  };

  const handleEditItem = (item) => {
    setEditItem({ ...item });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleUpdateItem = async (e) => {
    e.preventDefault();

    try {
      const oldStock = items.find(i => i.id === editItem.id)?.stock || 0;
      const newStock = Number(editItem.stock || 0);
      const stockDifference = newStock - oldStock;

      await axios.put(`${INVENTORY_API}/${editItem.id}`, {
        name: editItem.name,
        category: editItem.category,
        stock: Number(editItem.stock),
        price: Number(editItem.price),
        room_id: editItem.room_id || null,
        barcode: editItem.barcode || null,
      });

      // ? INSTANT UI UPDATE - Update item in state
      setItems(prev => prev.map(item => 
        item.id === editItem.id 
          ? { 
              ...item, 
              name: editItem.name,
              category: editItem.category,
              stock: Number(editItem.stock),
              price: Number(editItem.price),
              room_id: editItem.room_id || null,
              barcode: editItem.barcode || null,
              last_updated: new Date().toISOString()
            }
          : item
      ));
      console.log("? Item updated in UI state:", editItem.name);

      // ? LOG INVENTORY HISTORY (UPDATE transaction)
      try {
        await axios.post(`${INVENTORY_HISTORY_API}`, {
          inventory_id: editItem.id,
          product_name: editItem.name,
          category: editItem.category,
          total_in: stockDifference > 0 ? stockDifference : 0,
          total_out: stockDifference < 0 ? Math.abs(stockDifference) : 0,
          balance: newStock,
          last_source: "Adjustment",
          last_updated: new Date().toISOString(),
        });
      } catch (historyErr) {
        console.warn("?? Inventory history logging failed:", historyErr);
        // Don't fail the whole operation if history logging fails
      }

      setEditItem(null);
      void logEmployeeActivity({
        action: "Inventory Updated",
        module: "Inventory",
        description: `Updated inventory item "${editItem.name}" with stock ${newStock}.`,
      });
      alert(`? Item "${editItem.name}" updated successfully!`);
    } catch (err) {
      console.error("Error updating item:", err);
    }
  };

  const handleDeleteItem = async (id) => {
    if (!window.confirm("Delete this product from inventory?")) return;
    
    const itemToDelete = items.find(i => i.id === id);
    if (!itemToDelete) return;

    try {
      await axios.delete(`${INVENTORY_API}/${id}`);
      
      // ? INSTANT UI UPDATE - Remove item from state
      setItems(prev => prev.filter(item => item.id !== id));
      console.log("? Item deleted from UI state:", itemToDelete.name);
      
      // ? LOG INVENTORY HISTORY (DELETE transaction)
      try {
        await axios.post(`${INVENTORY_HISTORY_API}`, {
          inventory_id: id,
          product_name: itemToDelete.name,
          category: itemToDelete.category,
          total_in: 0,
          total_out: Number(itemToDelete.stock || 0),
          balance: 0,
          last_source: "Deleted",
          last_updated: new Date().toISOString(),
        });
      } catch (historyErr) {
        console.warn("?? Inventory history logging failed:", historyErr);
        // Don't fail the whole operation if history logging fails
      }

      void logEmployeeActivity({
        action: "Inventory Deleted",
        module: "Inventory",
        description: `Deleted inventory item "${itemToDelete.name}".`,
      });

      alert(`? Item "${itemToDelete.name}" deleted successfully!`);
    } catch (err) {
      console.error("Error deleting item:", err);
    }
  };

  /* ================= ROOMS ================= */
  const handleAddRoom = async (e) => {
    e.preventDefault();
    try {
      await axios.post(ROOMS_API, { room_name: newRoom });
      setNewRoom("");
      fetchRooms();
    } catch (err) {
      console.error("Error adding room:", err);
    }
  };

  const handleDeleteRoom = async (id) => {
    if (!window.confirm("Delete this room?")) return;
    try {
      await axios.delete(`${ROOMS_API}/${id}`);
      fetchRooms();
    } catch (err) {
      console.error("Error deleting room:", err);
    }
  };

  const handleEditRoom = (room) => {
    setEditRoomId(room.id);
    setEditRoomName(room.room_name || room.name || "");
  };

  const handleCancelRoomEdit = () => {
    setEditRoomId(null);
    setEditRoomName("");
  };

  const handleUpdateRoom = async (id) => {
    const trimmedName = String(editRoomName || "").trim();
    if (!trimmedName) return;

    try {
      await axios.put(`${ROOMS_API}/${id}`, { room_name: trimmedName });
      handleCancelRoomEdit();
      fetchRooms();
    } catch (err) {
      console.error("Error updating room:", err);
    }
  };

  const handleAddCategory = (e) => {
    e.preventDefault();
    const trimmedName = String(newCategoryName || "").trim();
    if (!trimmedName) return;

    const alreadyExists = categories.some(
      (category) => String(category.category || "").toLowerCase() === trimmedName.toLowerCase()
    );
    if (alreadyExists) {
      alert("Category already exists");
      return;
    }

    setCustomCategories((prev) => [...prev, trimmedName]);
    setNewCategoryName("");
  };

  const handleStartCategoryEdit = (category) => {
    setEditCategoryKey(category.key);
    setEditCategoryName(category.category);
  };

  const handleCancelCategoryEdit = () => {
    setEditCategoryKey(null);
    setEditCategoryName("");
  };

  const handleUpdateCategory = async (category) => {
    const trimmedName = String(editCategoryName || "").trim();
    if (!trimmedName) return;

    const duplicateExists = categories.some(
      (entry) =>
        entry.key !== category.key &&
        String(entry.category || "").toLowerCase() === trimmedName.toLowerCase()
    );
    if (duplicateExists) {
      alert("Category already exists");
      return;
    }

    const previousName = String(category.category || "").trim();
    const affectedItems = (items || []).filter(
      (item) => String(item.category || "").trim() === previousName
    );

    setItems((prev) =>
      prev.map((item) =>
        String(item.category || "").trim() === previousName
          ? { ...item, category: trimmedName }
          : item
      )
    );

    setCustomCategories((prev) => {
      const withoutPrevious = prev.filter((name) => String(name || "").trim() !== previousName);
      return affectedItems.length === 0 ? [...withoutPrevious, trimmedName] : withoutPrevious;
    });

    handleCancelCategoryEdit();

    for (const item of affectedItems) {
      try {
        await axios.put(`${INVENTORY_API}/${item.id}`, { ...item, category: trimmedName });
      } catch (err) {
        console.error("Error updating item category:", err);
      }
    }
  };

  const handleDeleteCategory = async (category) => {
    if (!window.confirm("Delete this category?")) return;

    const categoryName = String(category.category || "").trim();
    const fallbackCategory = "Uncategorized";
    const affectedItems = (items || []).filter(
      (item) => String(item.category || "").trim() === categoryName
    );

    if (affectedItems.length > 0) {
      setItems((prev) =>
        prev.map((item) =>
          String(item.category || "").trim() === categoryName
            ? { ...item, category: fallbackCategory }
            : item
        )
      );

      for (const item of affectedItems) {
        try {
          await axios.put(`${INVENTORY_API}/${item.id}`, { ...item, category: fallbackCategory });
        } catch (err) {
          console.error("Error reassigning item category:", err);
        }
      }
    }

    setCustomCategories((prev) => {
      const filtered = prev.filter((name) => String(name || "").trim() !== categoryName);
      return affectedItems.length > 0 && !filtered.includes(fallbackCategory)
        ? [...filtered, fallbackCategory]
        : filtered;
    });

    if (editCategoryKey === category.key) {
      handleCancelCategoryEdit();
    }
  };

  /* ================= CATEGORY LOGIC ================= */
  const categories = useMemo(() => {
    const categoryMap = (items || []).reduce((acc, item) => {
      const categoryName = String(item.category || "").trim();
      if (!categoryName) return acc;

      if (!acc[categoryName]) {
        acc[categoryName] = {
          key: categoryName,
          category: categoryName,
          totalStock: 0,
          products: 0,
          isCustom: false,
        };
      }
      acc[categoryName].totalStock += Number(item.stock || 0);
      acc[categoryName].products += 1;
      return acc;
    }, {});

    (customCategories || []).forEach((categoryName) => {
      const trimmedName = String(categoryName || "").trim();
      if (!trimmedName) return;

      if (!categoryMap[trimmedName]) {
        categoryMap[trimmedName] = {
          key: trimmedName,
          category: trimmedName,
          totalStock: 0,
          products: 0,
          isCustom: true,
        };
      }
    });

    return Object.values(categoryMap);
  }, [items, customCategories]);

  const getStockStatus = (item) => {
    const stock = Number(item.stock || 0);
    if (stock <= 0) return "OUT";
    if (stock <= 5) return "LOW";
    return "IN_STOCK";
  };

  const filteredItems = useMemo(() => {
    return (items || []).filter((i) => {
      if (filterName && !String(i.name || "").toLowerCase().includes(filterName.toLowerCase())) {
        return false;
      }
      if (filterBarcode && !String(i.barcode || "").toLowerCase().includes(filterBarcode.toLowerCase())) {
        return false;
      }
      if (filterStatus) {
        const st = getStockStatus(i);
        if (st !== filterStatus) return false;
      }
      if (filterRoom && String(i.room_id || "") !== String(filterRoom)) {
        return false;
      }
      if (filterAgeBucket) {
        const ageBucket = getCleanAgeBucket(getAgeDays(i.created_at));
        if (ageBucket !== filterAgeBucket) return false;
      }
      if (filterDate) {
        const d = i.created_at ? new Date(i.created_at) : null;
        if (!d || d.toISOString().slice(0, 10) !== filterDate) return false;
      }
      return true;
    });
  }, [
    items,
    filterName,
    filterBarcode,
    filterStatus,
    filterDate,
    filterAgeBucket,
    filterRoom,
  ]);

  const productTypeCount = useMemo(() => {
    return new Set(
      (items || [])
        .map((item) => String(item.name || "").trim().toLowerCase())
        .filter(Boolean)
    ).size;
  }, [items]);

  const evaluationSummary = useMemo(() => {
    const totalUnit = (items || []).reduce((sum, item) => sum + Number(item.stock || 0), 0);
    const totalEvaluation = (items || []).reduce(
      (sum, item) => sum + Number(item.stock || 0) * Number(item.price || 0),
      0
    );

    const formatCompactIndian = (value) => {
      const absoluteValue = Math.abs(Number(value || 0));
      if (absoluteValue >= 10000000) {
        return `${(value / 10000000).toFixed(2).replace(/\.00$/, "")} Cr`;
      }
      if (absoluteValue >= 100000) {
        return `${(value / 100000).toFixed(2).replace(/\.00$/, "")} Lakh`;
      }
      if (absoluteValue >= 1000) {
        return `${(value / 1000).toFixed(2).replace(/\.00$/, "")} K`;
      }
      return Number(value || 0).toFixed(2).replace(/\.00$/, "");
    };

    return {
      totalUnit,
      totalEvaluation,
      formattedEvaluation: formatCompactIndian(totalEvaluation),
    };
  }, [items]);

  const resetEvaluationModal = () => {
    setShowEvaluationModal(false);
    setEvaluationUnlocked(false);
    setEvaluationError("");
    setEvaluationPin(["", "", "", ""]);
  };

  const openEvaluationModal = () => {
    setShowEvaluationModal(true);
    setEvaluationUnlocked(false);
    setEvaluationError("");
    setEvaluationPin(["", "", "", ""]);
    setTimeout(() => {
      pinInputRefs.current[0]?.focus();
    }, 0);
  };

  const handleEvaluationPinChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;

    const nextPin = [...evaluationPin];
    nextPin[index] = value;
    setEvaluationPin(nextPin);
    setEvaluationError("");

    if (value && index < pinInputRefs.current.length - 1) {
      pinInputRefs.current[index + 1]?.focus();
    }
  };

  const handleEvaluationPinKeyDown = (index, event) => {
    if (event.key === "Backspace" && !evaluationPin[index] && index > 0) {
      pinInputRefs.current[index - 1]?.focus();
    }
  };

  const handleUnlockEvaluation = async () => {
    const pinValue = evaluationPin.join("");
    let isOverrideValid = false;

    try {
      isOverrideValid = Boolean(
        employeeId &&
          (await verifyPinOverride(employeeId, "inventory", pinValue))
      );
    } catch (error) {
      console.error("Inventory evaluation PIN verification failed:", error);
    }

    if (isOverrideValid) {
      setEvaluationUnlocked(true);
      setEvaluationError("");
      return;
    }
    setEvaluationError("Invalid PIN.");
  };

  const resetPriceUnlockModal = () => {
    setShowPriceUnlockModal(false);
    setPriceUnlockError("");
    setPricePin(["", "", "", ""]);
  };

  const openPriceUnlockModal = () => {
    if (pricesUnlocked) {
      setPricesUnlocked(false);
      return;
    }

    setShowPriceUnlockModal(true);
    setPriceUnlockError("");
    setPricePin(["", "", "", ""]);
    setTimeout(() => {
      pricePinInputRefs.current[0]?.focus();
    }, 0);
  };

  const handlePricePinChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;

    const nextPin = [...pricePin];
    nextPin[index] = value;
    setPricePin(nextPin);
    setPriceUnlockError("");

    if (value && index < pricePinInputRefs.current.length - 1) {
      pricePinInputRefs.current[index + 1]?.focus();
    }
  };

  const handlePricePinKeyDown = (index, event) => {
    if (event.key === "Backspace" && !pricePin[index] && index > 0) {
      pricePinInputRefs.current[index - 1]?.focus();
    }
  };

  const handleUnlockPrices = async () => {
    const pinValue = pricePin.join("");
    let isOverrideValid = false;

    try {
      isOverrideValid = Boolean(
        employeeId &&
          (await verifyPinOverride(employeeId, "inventory_price_unlock", pinValue))
      );
    } catch (error) {
      console.error("Inventory price PIN verification failed:", error);
    }

    if (isOverrideValid) {
      setPricesUnlocked(true);
      resetPriceUnlockModal();
      return;
    }
    setPriceUnlockError("Invalid PIN.");
  };

  const closeAddPinModal = () => {
    if (addPinVerifying) return;
    setShowAddPinModal(false);
    setAddPin(["", "", "", ""]);
    setAddPinError("");
  };

  const handleAddPinChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;

    const nextPin = [...addPin];
    nextPin[index] = value;
    setAddPin(nextPin);
    setAddPinError("");

    if (value && index < addPinInputRefs.current.length - 1) {
      addPinInputRefs.current[index + 1]?.focus();
    }
  };

  const handleAddPinKeyDown = (index, event) => {
    if (event.key === "Backspace" && !addPin[index] && index > 0) {
      addPinInputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyAddPin = async () => {
    const pinValue = addPin.join("");
    if (!/^\d{4}$/.test(pinValue)) {
      setAddPinError("Enter the 4-digit PIN.");
      return;
    }

    setAddPinVerifying(true);
    try {
      const isValid = Boolean(
        employeeId && (await verifyPinOverride(employeeId, "inventory_add", pinValue))
      );

      if (!isValid) {
        setAddPinError("Invalid PIN.");
        return;
      }

      setShowAddPinModal(false);
      setAddPinError("");
      await submitAddItem();
    } catch (error) {
      console.error("Inventory add PIN verification failed:", error);
      setAddPinError("Unable to verify PIN. Please try again.");
    } finally {
      setAddPinVerifying(false);
    }
  };

  const handleScan = () => {
    const code = (scanCode || "").trim();
    if (!code) return;
    const found = items.find((i) => String(i.barcode || "").trim() === code);
    if (!found) {
      alert("Product not found for scanned barcode");
      return;
    }
    setHighlightId(found.id);
    setTimeout(() => setHighlightId(null), 1500);
    setScanCode("");
  };

  const resetFilters = () => {
    setFilterName("");
    setFilterBarcode("");
    setFilterStatus("");
    setFilterDate("");
    setFilterAgeBucket("");
    setFilterRoom("");
  };

  const handleAcceptTransfer = async (transferId) => {
    try {
      setTransferLoading(true);
      await rtoAPI.acceptTransfer(transferId, "inventory");
      alert("Successfully moved to Inventory table");
      await Promise.all([fetchPendingTransfers(), fetchInventory()]);
    } catch (error) {
      console.error("Accept transfer failed:", error);
      alert("Failed to accept transfer");
    } finally {
      setTransferLoading(false);
    }
  };

  const handleRejectTransfer = async (transferId) => {
    try {
      setTransferLoading(true);
      await rtoAPI.rejectTransfer(transferId, "inventory");
      alert("Canceled");
      await fetchPendingTransfers();
    } catch (error) {
      console.error("Reject transfer failed:", error);
      alert("Failed to reject transfer");
    } finally {
      setTransferLoading(false);
    }
  };

  const handleAcceptAllTransfers = async () => {
    if (pendingTransfers.length === 0) return;
    try {
      setTransferLoading(true);
      const result = await rtoAPI.acceptAllTransfers("inventory");
      alert(result?.message || "All transfers accepted");
      await Promise.all([fetchPendingTransfers(), fetchInventory()]);
    } catch (error) {
      console.error("Accept all transfer failed:", error);
      alert("Failed to accept all transfers");
    } finally {
      setTransferLoading(false);
    }
  };

  return (
    <div className="page-container" aria-busy={loading}>

      {/* ===== HEADER ===== */}
      <div className="inventory-header">
        <h2>Inventory</h2>
        <div id="inventory-header-row" className="inventory-header-actions inventory-segmented-actions">
          <button
            type="button"
            className="inventory-header-btn inventory-header-btn-history"
            onClick={refreshData}
            disabled={loading}
          >
            <i className="fa-solid fa-rotate" aria-hidden="true"></i>
            {loading ? "Refreshing..." : "Refresh"}
          </button>
          <button
            type="button"
            className="inventory-header-btn inventory-header-btn-history inventory-product-type-btn"
            aria-label={`Product Type (${productTypeCount})`}
          >
            <i className="fa-solid fa-cube" aria-hidden="true"></i>
            Product Type ({productTypeCount})
          </button>
          <button
            type="button"
            id="inventory-settlement-top-btn"
            className="inventory-header-btn inventory-header-btn-history"
            onClick={() => navigate("/settlement")}
          >
            <i className="fa-solid fa-wallet" aria-hidden="true"></i>
            Settlement
          </button>
          <button
            type="button"
            id="inventory-history-top-btn"
            className="inventory-header-btn inventory-header-btn-history"
            onClick={() => navigate("/inventory/history")}
          >
            <i className="fa-solid fa-clock-rotate-left" aria-hidden="true"></i>
            Inventory History
          </button>
          <button
            type="button"
            id="inventory-evaluation-top-btn"
            className="inventory-header-btn inventory-header-btn-history"
            onClick={openEvaluationModal}
          >
            <i className="fa-solid fa-chart-line" aria-hidden="true"></i>
            Evaluation
          </button>
          <button
            type="button"
            id="inventory-unlock-price-top-btn"
            className="inventory-header-btn inventory-header-btn-history"
            onClick={openPriceUnlockModal}
          >
            <i className="fa-solid fa-lock-open" aria-hidden="true"></i>
            {pricesUnlocked ? "Lock Price" : "Unlock Price"}
          </button>
          <button
            type="button"
            id="inventory-rto-top-btn"
            className="inventory-header-btn inventory-header-btn-transfer"
            onClick={() => setShowTransferModal(true)}
          >
            <i className="fa-solid fa-arrow-right-arrow-left" aria-hidden="true"></i>
            Accept RTO Transfers ({pendingTransfers.length})
          </button>
        </div>
      </div>

      {error && (
        <div className="inventory-card" role="alert">
          {error}
        </div>
      )}

      {/* ===== EDIT BAR ===== */}
      {editItem && (
        <form className="edit-bar" onSubmit={handleUpdateItem}>
          <input value={editItem.name}
            onChange={(e) => setEditItem({ ...editItem, name: e.target.value })} required />
          <input value={editItem.category}
            onChange={(e) => setEditItem({ ...editItem, category: e.target.value })} required />
          <input type="number" value={editItem.stock}
            onChange={(e) => setEditItem({ ...editItem, stock: e.target.value })} required />
          <input type="number" value={editItem.price}
            onChange={(e) => setEditItem({ ...editItem, price: e.target.value })} required />

          <select value={editItem.room_id || ""}
            onChange={(e) => setEditItem({ ...editItem, room_id: e.target.value })}>
            <option value="">Select Room</option>
            {rooms.map((r) => (
              <option key={r.id} value={r.id}>{r.room_name || r.name}</option>
            ))}
          </select>

          <input value={editItem.barcode || ""}
            onChange={(e) => setEditItem({ ...editItem, barcode: e.target.value })} />

          <button className="save-btn">Update</button>
          <button type="button" className="cancel-btn" onClick={() => setEditItem(null)}>
            Cancel
          </button>
        </form>
      )}

      {/* ===== TABS ===== */}
      <div className="tabs">
        <button
          className={activeTab === "inventory" ? "active" : ""}
          onClick={() => setActiveTab("inventory")}
        >
          <i className="fa-solid fa-box-open" aria-hidden="true"></i>
          Inventory
        </button>
        <button
          className={activeTab === "rooms" ? "active" : ""}
          onClick={() => setActiveTab("rooms")}
        >
          <i className="fa-solid fa-door-open" aria-hidden="true"></i>
          Rooms
        </button>
        <button
          className={activeTab === "categories" ? "active" : ""}
          onClick={() => setActiveTab("categories")}
        >
          <i className="fa-solid fa-layer-group" aria-hidden="true"></i>
          Categories
        </button>
        <button
          className="generate-barcode-btn"
          onClick={() => navigate("/inventory/barcode")}
        >
          <i className="fa-solid fa-barcode" aria-hidden="true"></i>
          Generate Barcode
        </button>
      </div>

      {/* ===== INVENTORY TAB ===== */}
      {activeTab === "inventory" && (
        <div className="inventory-card inventory-shell-card">
          {/* ADD INVENTORY */}
          <form className="inventory-form inventory-add-form" onSubmit={handleAddItem}>
            <input placeholder="Product Name" value={newItem.name}
              onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} required />
            <input placeholder="Category" value={newItem.category}
              onChange={(e) => setNewItem({ ...newItem, category: e.target.value })} required />
            <input type="number" placeholder="Stock" value={newItem.stock}
              onChange={(e) => setNewItem({ ...newItem, stock: e.target.value })} required />
            <input type="number" placeholder="Price" value={newItem.price}
              onChange={(e) => setNewItem({ ...newItem, price: e.target.value })} required />

            <select value={newItem.room_id}
              onChange={(e) => setNewItem({ ...newItem, room_id: e.target.value })}>
              <option value="">Select Room</option>
              {rooms.map((r) => (
                <option key={r.id} value={r.id}>{r.room_name || r.name}</option>
              ))}
            </select>

            <select value={newItem.supplier_id}
              onChange={(e) => setNewItem({ ...newItem, supplier_id: e.target.value })}>
              <option value="">Select Supplier (Optional)</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name || s.supplier_name || s.company_name || `Supplier ${s.id}`}
                </option>
              ))}
            </select>

            <input placeholder="Barcode" value={newItem.barcode}
              onChange={(e) => setNewItem({ ...newItem, barcode: e.target.value })} />

            <button
              type="submit"
              id="inventory-add-btn"
              className="inventory-primary-btn"
            >
              Add Inventory
            </button>
          </form>

          {/* SCAN + FILTER BAR */}
          <div className="inventory-form inventory-filter-form" style={{ marginTop: 12 }}>
            <input
              placeholder="Scan / Enter Barcode to locate product"
              value={scanCode}
              onChange={(e) => setScanCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleScan();
                }
              }}
            />
            <button type="button" className="scan-btn" onClick={handleScan}>
              <i className="fa-solid fa-magnifying-glass"></i>
              Scan
            </button>

            <input
              placeholder="Filter by product name"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
            />
            <input
              placeholder="Filter by barcode"
              value={filterBarcode}
              autoComplete="off"
              name="inventory-filter-barcode"
              onChange={(e) => setFilterBarcode(e.target.value)}
            />
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="">All Status</option>
              <option value="IN_STOCK">In Stock</option>
              <option value="LOW">Low</option>
              <option value="OUT">Out</option>
            </select>
            <input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
            />
            <select value={filterAgeBucket} onChange={(e) => setFilterAgeBucket(e.target.value)}>
              <option value="">All Age Buckets</option>
              {CLEAN_AGE_BUCKET_OPTIONS.map((bucket) => (
                <option key={bucket} value={bucket}>
                  {bucket}
                </option>
              ))}
            </select>
            <select value={filterRoom} onChange={(e) => setFilterRoom(e.target.value)}>
              <option value="">All Rooms</option>
              {rooms.map((room) => (
                <option key={room.id} value={room.id}>
                  {room.room_name || room.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              id="inventory-reset-btn"
              className="inventory-primary-btn"
              onClick={resetFilters}
            >
              Reset Filters
            </button>
          </div>

          {/* TABLE */}
          <div className="inventory-table-wrap">
          <table className="inventory-table inventory-sales-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Category</th>
                <th>Barcode</th>
                <th>Stock</th>
                <th>Price</th>
                <th>Room</th>
                <th>Stock Value</th>
                <th>Status</th>
                <th>Created At</th>
                <th>Age</th>
                <th>Bucket</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.length === 0 ? (
                <tr>
                  <td colSpan="14" style={{ textAlign: "center" }}>
                    No items found
                  </td>
                </tr>
              ) : (
                filteredItems.map((i) => {
                  const ageDays = getAgeDays(i.created_at);
                  const ageBucket = getCleanAgeBucket(ageDays);
                  const stockStatus = getStockStatus(i);
                  const rowClass =
                    highlightId === i.id ? "row-highlight" :
                    stockStatus === "OUT" ? "row-out" :
                    stockStatus === "LOW" ? "row-low" : "";
                  return (
                    <tr key={i.id} className={rowClass}>
                      <td>{i.id}</td>
                      <td>{i.name}</td>
                      <td>{i.category}</td>
                      <td>{i.barcode}</td>
                      <td>{i.stock}</td>
                      <td>
                        {pricesUnlocked ? (
                          <>
                            {"\u20B9"}
                            {i.price}
                          </>
                        ) : (
                          <span className="inventory-locked-value">******</span>
                        )}
                      </td>
                      <td>
                        {rooms.find((r) => String(r.id) === String(i.room_id))?.room_name ||
                          rooms.find((r) => String(r.id) === String(i.room_id))?.name ||
                          "N/A"}
                      </td>
                      <td>
                        {pricesUnlocked ? (
                          <>
                            {"\u20B9"}
                            {(Number(i.stock || 0) * Number(i.price || 0)).toFixed(2)}
                          </>
                        ) : (
                          <span className="inventory-locked-value">******</span>
                        )}
                      </td>
                      <td>{stockStatus === "IN_STOCK" ? "In Stock" : stockStatus === "LOW" ? "Low" : "Out"}</td>
                      <td>{i.created_at ? new Date(i.created_at).toLocaleString() : "N/A"}</td>
                      <td>{ageDays}</td>
                      <td><span className={getCleanBucketClass(ageBucket)}>{ageBucket}</span></td>
                      <td className="inventory-actions-cell">
                        <button className="edit-btn" onClick={() => handleEditItem(i)}>Edit</button>
                        <button className="delete-btn" onClick={() => handleDeleteItem(i.id)}>Delete</button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* ===== ROOMS TAB ===== */}
      {activeTab === "rooms" && (
        <div className="inventory-card">
          <form className="inventory-form" onSubmit={handleAddRoom}>
            <input placeholder="Room Name" value={newRoom}
              onChange={(e) => setNewRoom(e.target.value)} required />
            <button type="submit">Add Room</button>
          </form>

          <table className="inventory-table">
            <tbody>
              {rooms.map((r) => (
                <tr key={r.id}>
                  <td>
                    {editRoomId === r.id ? (
                      <input
                        value={editRoomName}
                        onChange={(e) => setEditRoomName(e.target.value)}
                        placeholder="Room Name"
                      />
                    ) : (
                      r.room_name || r.name || "-"
                    )}
                  </td>
                  <td>
                    {editRoomId === r.id ? (
                      <>
                        <button
                          type="button"
                          className="edit-btn"
                          onClick={() => handleUpdateRoom(r.id)}
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          className="delete-btn"
                          onClick={handleCancelRoomEdit}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="edit-btn"
                          onClick={() => handleEditRoom(r)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="delete-btn"
                          onClick={() => handleDeleteRoom(r.id)}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ===== CATEGORIES TAB ===== */}
      {activeTab === "categories" && (
        <div className="inventory-card">
          <form className="inventory-form" onSubmit={handleAddCategory}>
            <input
              placeholder="Category Name"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              required
            />
            <button type="submit">Add Category</button>
          </form>

          <table className="inventory-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Total Products</th>
                <th>Total Stock</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((c) => (
                <tr key={c.key}>
                  <td>
                    {editCategoryKey === c.key ? (
                      <input
                        value={editCategoryName}
                        onChange={(e) => setEditCategoryName(e.target.value)}
                        placeholder="Category Name"
                      />
                    ) : (
                      c.category
                    )}
                  </td>
                  <td>{c.products}</td>
                  <td>{c.totalStock}</td>
                  <td className="inventory-actions-cell">
                    {editCategoryKey === c.key ? (
                      <>
                        <button
                          type="button"
                          className="edit-btn"
                          onClick={() => handleUpdateCategory(c)}
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          className="delete-btn"
                          onClick={handleCancelCategoryEdit}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="edit-btn"
                          onClick={() => handleStartCategoryEdit(c)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="delete-btn"
                          onClick={() => handleDeleteCategory(c)}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ===== PURCHASE HISTORY TAB ===== */}
      {activeTab === "purchase-history" && (
        <div className="inventory-card">
          <h3>Purchase History</h3>
          <p style={{ color: "#64748b", marginBottom: "20px" }}>
            View all purchase transactions and inventory acquisitions.
          </p>
          
          {purchaseHistory.length === 0 ? (
            <div style={{ 
              padding: "40px", 
              textAlign: "center", 
              backgroundColor: "#f8fafc", 
              borderRadius: "8px",
              border: "2px dashed #cbd5e1"
            }}>
              <div style={{ fontSize: "48px", marginBottom: "16px" }}>??</div>
              <h4 style={{ color: "#475569", marginBottom: "8px" }}>No Purchase History</h4>
              <p style={{ color: "#64748b", marginBottom: "16px" }}>
                No purchase records found. Start adding items with suppliers to see purchase history.
              </p>
            </div>
          ) : (
            <table className="inventory-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Product</th>
                  <th>Category</th>
                  <th>Supplier</th>
                  <th>Quantity</th>
                  <th>Unit Price</th>
                  <th>Total Amount</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {purchaseHistory.map((purchase, idx) => (
                  <tr key={idx}>
                    <td>{purchase.purchase_date ? new Date(purchase.purchase_date).toLocaleDateString() : "N/A"}</td>
                    <td>{purchase.product_name}</td>
                    <td>{purchase.category}</td>
                    <td>{suppliers.find(s => s.id === purchase.supplier_id)?.name || "Unknown"}</td>
                    <td>{purchase.quantity}</td>
                    <td>?{purchase.unit_price}</td>
                    <td>?{purchase.total_amount}</td>
                    <td>{purchase.notes || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ===== SUPPLIERS TAB ===== */}
      {activeTab === "suppliers" && (
        <div className="inventory-card">
          <h3>Supplier Management</h3>
          <p style={{ color: "#64748b", marginBottom: "20px" }}>
            Add and manage your suppliers for purchase tracking.
          </p>
          
          {/* ADD SUPPLIER FORM */}
          <form className="inventory-form" onSubmit={async (e) => {
            e.preventDefault();
            if (!newSupplier.name.trim()) {
              alert("Supplier name is required");
              return;
            }
            
            try {
              const res = await axios.post(SUPPLIERS_API, newSupplier);
              console.log("Supplier added:", res.data);
              setSuppliers([...suppliers, res.data]);
              setNewSupplier({
                name: "",
                contact: "",
                email: "",
                address: ""
              });
              alert("? Supplier added successfully!");
              await fetchSuppliers();
            } catch (err) {
              console.error("Error adding supplier:", err);
              alert("Failed to add supplier: " + (err?.response?.data?.message || err?.message));
            }
          }}>
            <input 
              placeholder="Supplier Name" 
              value={newSupplier.name}
              onChange={(e) => setNewSupplier({ ...newSupplier, name: e.target.value })} 
              required 
            />
            <input 
              placeholder="Contact Number" 
              value={newSupplier.contact}
              onChange={(e) => setNewSupplier({ ...newSupplier, contact: e.target.value })} 
            />
            <input 
              placeholder="Email Address" 
              type="email"
              value={newSupplier.email}
              onChange={(e) => setNewSupplier({ ...newSupplier, email: e.target.value })} 
            />
            <input 
              placeholder="Address" 
              value={newSupplier.address}
              onChange={(e) => setNewSupplier({ ...newSupplier, address: e.target.value })} 
            />
            <button type="submit">Add Supplier</button>
          </form>

          {/* SUPPLIERS TABLE */}
          <table className="inventory-table" style={{ marginTop: "20px" }}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Contact</th>
                <th>Email</th>
                <th>Address</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {suppliers.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: "center", padding: "40px" }}>
                    <div style={{ fontSize: "48px", marginBottom: "16px" }}>??</div>
                    <h4 style={{ color: "#475569", marginBottom: "8px" }}>No Suppliers</h4>
                    <p style={{ color: "#64748b" }}>
                      Add your first supplier to start tracking purchases.
                    </p>
                  </td>
                </tr>
              ) : (
                suppliers.map((supplier) => (
                  <tr key={supplier.id}>
                    <td>{supplier.name}</td>
                    <td>{supplier.contact || "-"}</td>
                    <td>{supplier.email || "-"}</td>
                    <td>{supplier.address || "-"}</td>
                    <td>
                      <button 
                        className="delete-btn" 
                        onClick={async () => {
                          if (window.confirm(`Are you sure you want to delete supplier "${supplier.name}"?`)) {
                            try {
                              await axios.delete(`${SUPPLIERS_API}/${supplier.id}`);
                              setSuppliers(suppliers.filter(s => s.id !== supplier.id));
                              alert("? Supplier deleted successfully!");
                            } catch (err) {
                              console.error("Error deleting supplier:", err);
                              alert("Failed to delete supplier: " + (err?.response?.data?.message || err?.message));
                            }
                          }
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {showEvaluationModal && (
        <div className="modal-overlay">
          <div className={`inventory-evaluation-modal ${evaluationUnlocked ? "expanded" : ""}`}>
            <div className="inventory-evaluation-header">
              <h3>{evaluationUnlocked ? "Inventory Evaluation" : "Unlock Inventory Evaluation"}</h3>
              <button
                type="button"
                className="inventory-evaluation-close"
                onClick={resetEvaluationModal}
              >
                X
              </button>
            </div>

            {!evaluationUnlocked ? (
              <div className="inventory-evaluation-body">
                <p>Enter the 4-digit PIN to reveal inventory evaluation.</p>
                <div className="inventory-pin-grid">
                  {evaluationPin.map((digit, index) => (
                    <input
                      key={index}
                      ref={(element) => {
                        pinInputRefs.current[index] = element;
                      }}
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      name={`evaluation-pin-${index}`}
                      pattern="[0-9]*"
                      maxLength={1}
                      value={digit}
                      className="inventory-pin-input"
                      onChange={(event) => handleEvaluationPinChange(index, event.target.value)}
                      onKeyDown={(event) => handleEvaluationPinKeyDown(index, event)}
                    />
                  ))}
                </div>
                {evaluationError && <div className="inventory-evaluation-error">{evaluationError}</div>}
                <button
                  type="button"
                  className="inventory-evaluation-submit"
                  onClick={handleUnlockEvaluation}
                >
                  Unlock
                </button>
              </div>
            ) : (
              <div className="inventory-evaluation-cards">
                <div className="inventory-evaluation-card">
                  <span className="inventory-evaluation-label">Total Unit</span>
                  <strong>{evaluationSummary.totalUnit.toLocaleString("en-IN")}</strong>
                </div>
                <div className="inventory-evaluation-card">
                  <span className="inventory-evaluation-label">Total Evaluation</span>
                  <strong>₹{evaluationSummary.formattedEvaluation}</strong>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showPriceUnlockModal && (
        <div className="modal-overlay">
          <div className="inventory-evaluation-modal">
            <div className="inventory-evaluation-header">
              <h3>Unlock Price</h3>
              <button
                type="button"
                className="inventory-evaluation-close"
                onClick={resetPriceUnlockModal}
              >
                X
              </button>
            </div>

            <div className="inventory-evaluation-body">
              <p>Enter the 4-digit PIN to reveal price and stock value.</p>
              <div className="inventory-pin-grid">
                {pricePin.map((digit, index) => (
                  <input
                    key={index}
                    ref={(element) => {
                      pricePinInputRefs.current[index] = element;
                    }}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    name={`price-unlock-pin-${index}`}
                    pattern="[0-9]*"
                    maxLength={1}
                    value={digit}
                    className="inventory-pin-input"
                    onChange={(event) => handlePricePinChange(index, event.target.value)}
                    onKeyDown={(event) => handlePricePinKeyDown(index, event)}
                  />
                ))}
              </div>
              {priceUnlockError && <div className="inventory-evaluation-error">{priceUnlockError}</div>}
              <button
                type="button"
                className="inventory-evaluation-submit"
                onClick={handleUnlockPrices}
              >
                Unlock
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddPinModal && (
        <div className="modal-overlay">
          <div className="inventory-evaluation-modal">
            <div className="inventory-evaluation-header">
              <h3>Verify PIN to Add Inventory</h3>
              <button
                type="button"
                className="inventory-evaluation-close"
                onClick={closeAddPinModal}
                disabled={addPinVerifying}
              >
                X
              </button>
            </div>
            <div className="inventory-evaluation-body">
              <p>Enter your 4-digit Add Inventory master PIN.</p>
              <div className="inventory-pin-grid">
                {addPin.map((digit, index) => (
                  <input
                    key={index}
                    ref={(element) => {
                      addPinInputRefs.current[index] = element;
                    }}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    name={`add-inventory-pin-${index}`}
                    pattern="[0-9]*"
                    maxLength={1}
                    value={digit}
                    className="inventory-pin-input"
                    onChange={(event) => handleAddPinChange(index, event.target.value)}
                    onKeyDown={(event) => handleAddPinKeyDown(index, event)}
                    disabled={addPinVerifying}
                  />
                ))}
              </div>
              {addPinError && <div className="inventory-evaluation-error">{addPinError}</div>}
              <button
                type="button"
                className="inventory-evaluation-submit"
                onClick={handleVerifyAddPin}
                disabled={addPinVerifying}
              >
                {addPinVerifying ? "Verifying..." : "Verify & Add"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showTransferModal && (
        <div className="modal-overlay">
          <div className="modal-card transfer-modal">
            <div className="modal-header">
              <h3>Pending Inventory Transfers</h3>
              <button className="cancel-btn" onClick={() => setShowTransferModal(false)}>
                Close
              </button>
            </div>
            <div className="transfer-actions">
              <button
                className="save-btn"
                onClick={handleAcceptAllTransfers}
                disabled={transferLoading || pendingTransfers.length === 0}
              >
                {transferLoading ? "Processing..." : "Accept All"}
              </button>
            </div>
            <div className="transfer-table-wrap">
              <table className="inventory-table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Product</th>
                    <th>Qty</th>
                    <th>Value</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingTransfers.length === 0 ? (
                    <tr>
                      <td colSpan="5" style={{ textAlign: "center", padding: 16 }}>
                        No pending transfers
                      </td>
                    </tr>
                  ) : (
                    pendingTransfers.map((request) => (
                      <tr key={request.id}>
                        <td>{request.orderId}</td>
                        <td>{request?.payload?.productName || "-"}</td>
                        <td>{request?.payload?.qty || 1}</td>
                        <td>?{Number(request?.payload?.value || 0).toLocaleString("en-IN")}</td>
                        <td style={{ display: "flex", gap: 8 }}>
                          <button
                            className="save-btn"
                            onClick={() => handleAcceptTransfer(request.id)}
                            disabled={transferLoading}
                          >
                            Accept
                          </button>
                          <button
                            className="cancel-btn"
                            onClick={() => handleRejectTransfer(request.id)}
                            disabled={transferLoading}
                          >
                            Reject
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
